import {
  MainContent,
  PageWrapper,
  Header,
  Meta,
  Custom404,
  MobileToggleControls,
  SearchBox,
  BreadcrumbNavigation,
} from '../../../../components';
import {
  Loading as LoadingSpinner,
  Button,
  useVisibility,
  ListingListItem,
  Spinner,
} from '@requity-homes/component-library';
import {
  getListingsEnvironmentBaseUrl,
  removeEmptyValues,
  cityCoordinates,
  capitalizeAllFirstLetters,
  getBoundByCenter,
  RepliersStateToAbbreviatedProvinceMap,
  cookiesIdToken,
  cookiesFavoriteListings,
  getCenterByBound,
} from '@requity-homes/utils';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import { FilterPanel } from '../../../../components/filter-panel/filter-panel';
import MapBox from '../../../../components/map/mapbox';

import {
  useSearchListingsLazyQuery,
  Listing,
  SearchListingsFilterValues,
  SearchListingsDocument,
} from '../../../../graphql/generated';
import {
  cityToAbbreviatedProvinceMap,
  queryParamsToCityMap,
  generateStructuredData,
  useIsMobileView,
  prepareSearchFilterValues,
  getFilterValuesFromQuery,
  redirectToCustomerPortal,
} from '../../../../utils';
import { client } from '../../../../graphql/apollo-client';
import { Pagination } from '../../../../components';

import { GetServerSidePropsContext } from 'next';
import { ParsedUrlQuery } from 'querystring';
import mapboxgl from 'mapbox-gl';
import { twMerge } from 'tailwind-merge';
import * as React from 'react';
import { LISTINGS_PER_PAGE } from '../../../../const';
import { validateSearchQueryParams } from '../../../../utils/validate-search-query-params';
import { getCookie } from 'cookies-next';
import { useFavoriteButtonClick } from '../../../../hooks';

const repliersCdnUrl = process.env.NEXT_PUBLIC_REPLIER_CDN_URL;

const idToken = getCookie(cookiesIdToken);

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const city = context.params.city as string;
  const { query } = context;

  const clientIp = (
    context.req.headers['cloudfront-viewer-address'] as string
  )?.split(':')[0];
  console.log('cloudfront-viewer-address', clientIp);

  const filterValues = getFilterValuesFromQuery(query, city);
  console.log('city', city);
  console.log('query', query);
  console.log('filterValues', filterValues);
  console.log(
    'prepareSearchFilterValues(filterValues)',
    prepareSearchFilterValues(filterValues),
  );

  const { data } = await client.query({
    query: SearchListingsDocument,
    variables: {
      filterValues: prepareSearchFilterValues(filterValues),
      clientIp,
    },
    fetchPolicy: 'no-cache',
  });

  const { listings = [], clusters = [], ...rest } = data.searchListings || {};

  const statusCode = validateSearchQueryParams(query.city, query.province);
  context.res.statusCode = statusCode;

  return {
    props: {
      city: queryParamsToCityMap[city],
      filterValues,
      listings,
      clusters,
      listingsStats: rest,
      statusCode,
    },
  };
}

export default function ListingsPage({
  listings: initialListings,
  city: initialCity,
  filterValues: initialFilterValues,
  listingsStats: initialListingsStats,
  clusters: initialClusters,
  statusCode,
}) {
  const router = useRouter();
  const isMobileView = useIsMobileView();

  const [selectedListingId, setSelectedListingId] = useState<string>(null);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [query, setQuery] = useState(router.query);

  useEffect(() => {
    setQuery(() => router.query);
  }, [router.query]);

  const initialMapBounds = initialFilterValues.boundary
    ? {
        _sw: {
          lng: parseFloat(initialFilterValues.boundary.split(',')[0]),
          lat: parseFloat(initialFilterValues.boundary.split(',')[1]),
        },
        _ne: {
          lng: parseFloat(initialFilterValues.boundary.split(',')[2]),
          lat: parseFloat(initialFilterValues.boundary.split(',')[3]),
        },
      }
    : null;

  const [mapBounds, setMapBounds] = useState(initialMapBounds);
  const [isListVisible, setIsListVisible] = useVisibility(false);
  const [isFiltersVisible, setIsFiltersVisible] = useVisibility(false);
  const [listingsStats, setListingsStats] = useState<any>(initialListingsStats);
  const [city, setCity] = useState<string>(initialCity);
  const [isSearchBoxFocused, setIsSearchBoxFocused] = useState(false);

  useEffect(() => {
    if (isMobileView) {
      setIsListVisible(true);
    }
  }, [isMobileView]);

  const {
    isLoading,
    favoriteListingIds,
    setFavoriteListingIds,
    toggleListings,
  } = useFavoriteButtonClick();

  useEffect(() => {
    const listingIds = getCookie(cookiesFavoriteListings);
    if (listingIds) {
      setFavoriteListingIds(JSON.parse(listingIds));
    }
  }, []);

  const [filterValues, setFilterValues] =
    useState<SearchListingsFilterValues>(initialFilterValues);
  const [activeListing, setActiveListing] = useState<Listing | null>(null);
  const [centerCoordinates, setCenterCoordinates] = useState(
    cityCoordinates[router.query.city as string],
  );

  const [listingsData, setListingsData] = useState({
    center: cityCoordinates[router.query.city as string],
    listings: initialListings || [],
    clusters: initialClusters || [],
    polygons: [],
    mapBounds: initialMapBounds
      ? [
          [initialMapBounds?._sw.lng, initialMapBounds?._sw.lat],
          [initialMapBounds?._ne.lng, initialMapBounds?._ne.lat],
        ]
      : null,
  });

  const [
    searchListings,
    { loading: loadingListingData, data: loadedListingData },
  ] = useSearchListingsLazyQuery();

  const toggleFilters = () => setIsFiltersVisible(!isFiltersVisible);
  const toggleList = () => setIsListVisible(!isListVisible);

  const searchByBoundary = useCallback(
    (newMapBounds: {
      _sw: { lng: number; lat: number };
      _ne: { lng: number; lat: number };
    }) => {
      if (!newMapBounds) {
        return;
      }

      if (newMapBounds instanceof mapboxgl.LngLatBounds) {
        setMapBounds(newMapBounds as any);
      } else {
        setMapBounds(
          new mapboxgl.LngLatBounds(newMapBounds._sw, newMapBounds._ne) as any,
        );
      }

      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            page: '1',
            boundary:
              newMapBounds._sw.lng +
              ',' +
              newMapBounds._sw.lat +
              ',' +
              newMapBounds._ne.lng +
              ',' +
              newMapBounds._ne.lat,
          },
        },
        undefined,
        { shallow: false },
      );
    },
    [query, router],
  );

  const handleUpdateFilters = useCallback(
    async (values) => {
      const cleanedParams = removeEmptyValues(values);

      const city = cleanedParams.city;
      delete cleanedParams.city;

      const queryParams = new URLSearchParams({
        ...cleanedParams,
        page: '1',
      });

      const cityName = capitalizeAllFirstLetters(city.replace(/-/g, ' '));
      // TODO: Rework this to use router.push
      window.location.href = `${router.basePath}/${cityToAbbreviatedProvinceMap[
        cityName
      ].toLowerCase()}/${city
        .toLowerCase()
        .replace(/ /g, '-')}/rent-to-own?${queryParams.toString()}`;
    },
    [router],
  );

  useEffect(() => {
    (async () => {
      const queryFilterValues = getFilterValuesFromQuery(
        router.query as ParsedUrlQuery,
      );
      if (isEqual(filterValues, queryFilterValues)) {
        return;
      }
      setCity(queryFilterValues.city);
      setFilterValues(queryFilterValues);

      await searchListings({
        variables: {
          filterValues: prepareSearchFilterValues(queryFilterValues),
          clientIp: '',
        },
      });
    })();
  }, [filterValues, router.query, searchListings]);

  useEffect(() => {
    if (loadedListingData) {
      const { listings, clusters, ...rest } =
        loadedListingData.searchListings || {};

      const center = mapBounds
        ? getCenterByBound(mapBounds as any)
        : cityCoordinates[city];

      setListingsData({
        center,
        listings: listings || [],
        clusters: clusters || [],
        mapBounds: mapBounds as any,
        polygons: [],
      });
      setListingsStats(rest);

      if (listings?.length > 0) {
        setCity(listings[0].address.city);
      }
    }
  }, [loadedListingData, city, router, centerCoordinates, mapBounds]);

  const totalListings = useMemo(() => {
    let count = listingsData.clusters.reduce(
      (acc, cluster) => acc + cluster.count,
      0,
    );

    if (count < LISTINGS_PER_PAGE) {
      count = listingsData.listings.length;
    }

    return count;
  }, [listingsData.clusters, listingsData.listings]);

  useEffect(() => {
    if (selectedListingId) {
      const foundListing = listingsData.listings.find(
        (lst) => lst.mlsNumber === selectedListingId,
      );
      setSelectedListing(foundListing);
    }
  }, [selectedListingId, listingsData]);

  const baseUrl = `${getListingsEnvironmentBaseUrl(
    process.env.NEXT_PUBLIC_STAGE,
  )}${router.pathname}`.split('/[province]/')[0];

  if (statusCode == 404) {
    return <Custom404 />;
  }

  let ogUrl = '';
  let province = '';
  if (listingsData.listings.length > 0) {
    const firstListing = listingsData.listings[0];
    province = firstListing.address.state;
    ogUrl = `${baseUrl}/${
      RepliersStateToAbbreviatedProvinceMap[province]
    }/${city.toLowerCase().replace(/ /g, '-')}/rent-to-own`;
  }

  const pageTitle = `${totalListings} Rent-to-Own Homes in ${capitalizeAllFirstLetters(
    city,
  )}, ${capitalizeAllFirstLetters(province)} | Requity Homes`;

  const structuredData = generateStructuredData(listingsData.listings);

  const handleFavoriteBtnClick = async (list) => {
    if (!idToken) {
      return redirectToCustomerPortal();
    }

    await toggleListings(list);
  };

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.requityhomes.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Browse Homes',
        item: 'https://www.requityhomes.com/listings',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${city}, ${province}`,
        item: ogUrl,
      },
    ],
  };

  const description = `View ${totalListings} rent-to-own listings and real estate for sale in ${capitalizeAllFirstLetters(
    city,
  )}, ${
    cityToAbbreviatedProvinceMap[city]
  }. Find the best rent-to-own houses for sale near you with down payments as low as 2%`;

  const headingText = `${totalListings} rent-to-own listings in ${capitalizeAllFirstLetters(
    city.replace(/-/g, ' '),
  )}`;

  const showPagination = totalListings > LISTINGS_PER_PAGE;

  return (
    <PageWrapper>
      <Meta
        title={pageTitle}
        description={description}
        ogImage="https://brand-assets.requityhomes.com/open-graph-city-image.png"
        keywords={`Rent to Own, Homes, ${city}`}
        ogUrl={ogUrl}
        structuredData={structuredData}
        breadcrumbList={breadcrumbList}
      />
      <MainContent>
        <div className="fixed top-0 bottom-0 w-full h-full bg-gray-100">
          {isLoading && <Spinner />}
          <Header city={city} />
          <div className="flex flex-col md:flex-row relative">
            <div
              className={twMerge(
                'relative w-full md:w-1/2 h-[calc(100vh-6rem)]',
                isListVisible || isFiltersVisible
                  ? 'hidden md:block'
                  : 'md:block',
              )}
            >
              <div
                className={twMerge(
                  'items-center justify-center text-grey-3 absolute z-10 w-full h-full backdrop-blur',
                  loadingListingData ? 'flex' : 'hidden',
                )}
              >
                <LoadingSpinner className="w-8 h-8 sm:w-10 sm:h-10" />
              </div>
              <MapBox
                className={isListVisible ? 'hidden' : 'block'}
                searchButtonClassName="mt-14 md:mt-0 whitespace-nowrap"
                data={listingsData}
                onMapBoundsChanged={(newMapBounds) => {
                  searchByBoundary(newMapBounds);
                }}
                onMarkerClick={(event) => {
                  const listingId = (event.target as HTMLElement).attributes[
                    'data-listing-id'
                  ];
                  // find the listing by data-listing-id and scroll to it
                  const listing = document.querySelector(
                    `[data-listing-id="${listingId}"]`,
                  );

                  if (!isMobileView) {
                    listing?.scrollIntoView({ behavior: 'smooth' });
                  }
                  setSelectedListingId(listingId);
                }}
                activeListing={activeListing}
                isFiltersVisible={isFiltersVisible}
              />
              {selectedListing && (
                <div
                  className="absolute top-0 w-full h-full flex items-center justify-center bg-black bg-opacity-30 md:hidden z-30"
                  onClick={() => {
                    setSelectedListing(null);
                    setSelectedListingId(null);
                  }}
                >
                  <div className="mt-24">
                    <ListingListItem
                      key={selectedListing.mlsNumber}
                      selectedListingId={selectedListingId}
                      setSelectedListingId={setSelectedListingId}
                      repliersCdnUrl={repliersCdnUrl}
                      listing={selectedListing}
                      setActiveListing={setActiveListing}
                      isRenderedOnMap
                    />
                  </div>
                </div>
              )}
              <SearchBox
                className={twMerge(
                  'absolute top-2 md:top-3.5 md:w-full flex flex-col md:items-center transition-all duration-500 ease-in-out left-2 md:left-0',
                  `${isSearchBoxFocused ? 'w-full -ml-2' : 'w-24 md:w-full'}`,
                )}
                inputClassName={`${
                  isSearchBoxFocused ? 'w-full' : 'w-12'
                } md:w-3/4`}
                onBlur={() => {
                  setIsSearchBoxFocused(false);
                }}
                onFocus={() => {
                  setIsSearchBoxFocused(true);
                }}
                centerLocation={centerCoordinates}
                handleAreaChange={({ lat, lng }) => {
                  setCenterCoordinates({ lat, lng });
                  const bound = getBoundByCenter({ lat, lng }, 1);
                  return searchByBoundary({
                    _sw: { lng: bound[0], lat: bound[1] },
                    _ne: { lng: bound[2], lat: bound[3] },
                  });
                }}
              />
            </div>
            {!isFiltersVisible && (
              <MobileToggleControls
                className={`transition-opacity duration-500 ease-in-out 
                  `}
                toggleFilters={toggleFilters}
                toggleList={toggleList}
                showList={!isListVisible}
                totalListings={totalListings}
                listingsStats={listingsStats}
                listingsData={listingsData}
                filterValues={filterValues}
                isSearchBoxFocused={isSearchBoxFocused}
                setIsSearchBoxFocused={setIsSearchBoxFocused}
                centerCoordinates={centerCoordinates}
                setCenterCoordinates={setCenterCoordinates}
                getBoundByCenter={getBoundByCenter}
                searchByBoundary={searchByBoundary}
                isListVisible={isListVisible}
                headingText={headingText}
              />
            )}
            <div className="w-full md:w-1/2 overflow-y-auto mt-1 bg-gray-100 pt-0 md:pt-0 h-[calc(100vh-6rem)]">
              {!isFiltersVisible && (
                <>
                  <div className="p-6 pt-4 relative">
                    <div className="fixed hidden md:block left-1/2right-0 z-10 -ml-2 pl-2 -mt-4 bg-gray-100 pt-4 pb-3 pr-8 w-[calc(50%-2rem)]">
                      <BreadcrumbNavigation province={province} />
                      <div className="hidden md:flex justify-between items-center">
                        <h1 className="text-xl font-semibold md:text-2xl md:font-bold">
                          {headingText}
                        </h1>
                        <Button
                          className="mx-0 bg-white active:bg-white shadow-md-dark text-coral-med focus:outline-none border-transparent"
                          onClick={() => toggleFilters()}
                        >
                          Filters
                        </Button>
                      </div>
                      {showPagination && (
                        <Pagination
                          listingsStats={listingsStats}
                          listingsData={listingsData}
                          filterValues={filterValues}
                        />
                      )}
                    </div>
                    <div
                      className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
                        showPagination ? 'md:mt-32' : 'md:mt-28'
                      }`}
                    >
                      {listingsData.listings?.map((listing: Listing) => (
                        <ListingListItem
                          key={listing.mlsNumber}
                          selectedListingId={selectedListingId}
                          setSelectedListingId={setSelectedListingId}
                          repliersCdnUrl={repliersCdnUrl}
                          listing={listing}
                          setActiveListing={setActiveListing}
                          favoriteListingIds={favoriteListingIds}
                          handleFavoriteBtnClick={handleFavoriteBtnClick}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
              {isFiltersVisible && (
                <FilterPanel
                  title={'Filters'}
                  values={filterValues}
                  toggleFilters={toggleFilters}
                  updateFilters={handleUpdateFilters}
                />
              )}
            </div>
          </div>
        </div>
      </MainContent>
    </PageWrapper>
  );
}
